import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Download,
  FileText,
  Plus,
  Receipt,
  Search,
  Send,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  ContextHero,
  KpiCard,
  MoneyAmount,
  EmptyStateModern,
  PageContainer,
  SectionTitle,
} from '@/components/design-system';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useUsersList } from '@/hooks/queries/useUsersList';
import {
  useRecouvrementEntreprises,
  useDeleteFacture,
  type FactureRecouvrementRow,
  type RecouvrementStatut,
} from '@/hooks/queries/useRecouvrementEntreprises';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ImportRecouvrementEntreprisesDialog } from '@/components/imports/ImportRecouvrementEntreprisesDialog';
import { CreateFactureRecouvrementDialog } from '@/components/forms/CreateFactureRecouvrementDialog';
import { EnregistrerEncaissementDialog } from '@/components/forms/EnregistrerEncaissementDialog';
import { MarquerFacturesEncaisseesEnLotDialog } from '@/components/forms/MarquerFacturesEncaisseesEnLotDialog';
import { GeneratePdfFactureDialog } from '@/components/forms/GeneratePdfFactureDialog';
import { PlanPaiementDepuisFactureDialog } from '@/components/forms/PlanPaiementDepuisFactureDialog';
import { FactureRecouvrementDrawer } from '@/components/finances/FactureRecouvrementDrawer';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import { RelancerDialog, type RelanceTarget } from '@/routes/echeances/RelancerDialog';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';

const STATUT_LABEL: Record<RecouvrementStatut, string> = {
  a_encaisser: 'À encaisser',
  partiel: 'Partiel',
  regle: 'Réglé',
};

const STATUT_FILTERS: { value: RecouvrementStatut | ''; label: string }[] = [
  { value: '', label: 'Tous' },
  { value: 'a_encaisser', label: 'À encaisser' },
  { value: 'partiel', label: 'Partiels' },
  { value: 'regle', label: 'Réglés' },
];

function mapStatut(s: RecouvrementStatut): 'a_payer' | 'partiel' | 'paye' {
  if (s === 'a_encaisser') return 'a_payer';
  if (s === 'regle') return 'paye';
  return 'partiel';
}

export function RecouvrementEntreprisesPage(): JSX.Element {
  const canView = usePermission('recouvrement.view');
  const canEdit = usePermission('recouvrement.edit');
  const { data: entreprises = [] } = useEntreprises();
  const { data: activites = [] } = useAllActivites();
  const { data: users = [] } = useUsersList();

  const eurl =
    entreprises.find((e) => /kost/i.test(e.nom ?? '') && /eurl/i.test(e.nom ?? '')) ??
    entreprises[0] ??
    null;
  const [entrepriseId, setEntrepriseId] = useState<string>('');
  const effectiveEntrepriseId = entrepriseId || eurl?.id || '';
  const activiteB2B = useMemo(
    () =>
      activites.find(
        (a) => a.entreprise_id === effectiveEntrepriseId && /b2b|formation b2b/i.test(a.nom ?? ''),
      ),
    [activites, effectiveEntrepriseId],
  );

  const [annee, setAnnee] = useState<number | ''>('');
  const [statut, setStatut] = useState<RecouvrementStatut | ''>('');
  const [commercialId, setCommercialId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editFacture, setEditFacture] = useState<FactureRecouvrementRow | null>(null);
  const [encaisserFacture, setEncaisserFacture] = useState<FactureRecouvrementRow | null>(null);
  const [pdfFacture, setPdfFacture] = useState<FactureRecouvrementRow | null>(null);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FactureRecouvrementRow | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [drawerFacture, setDrawerFacture] = useState<FactureRecouvrementRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Sprint 6.9.13 — relances depuis Recouvrement
  const [relanceFacture, setRelanceFacture] = useState<FactureRecouvrementRow | null>(null);
  const [bulkRelance, setBulkRelance] = useState(false);
  // Sprint 6.9.15 — plan de paiement depuis Recouvrement
  const [planFacture, setPlanFacture] = useState<FactureRecouvrementRow | null>(null);

  const { data: factures = [], isLoading } = useRecouvrementEntreprises({
    entrepriseId: effectiveEntrepriseId || null,
    annee: annee === '' ? null : annee,
    statut: statut || null,
    commercialId: commercialId || null,
    search,
  });
  const deleteFacture = useDeleteFacture();

  const yearsAvailable = useMemo(() => {
    const set = new Set<number>();
    const current = new Date().getFullYear();
    for (let y = 2020; y <= current + 1; y++) set.add(y);
    for (const f of factures) set.add(f.annee);
    return Array.from(set).sort((a, b) => b - a);
  }, [factures]);

  const selectedFactures = useMemo(
    () => factures.filter((f) => selectedIds.has(f.id)),
    [factures, selectedIds],
  );

  const kpis = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const facturesYTD = factures.filter((f) => f.annee === currentYear);
    const caYTD = facturesYTD.reduce((a, f) => a + f.montant_ttc, 0);
    const encaisseYTD = facturesYTD.reduce((a, f) => a + f.montant_encaisse, 0);
    const aRecouvrer = factures
      .filter((f) => f.statut !== 'regle')
      .reduce((a, f) => a + (f.montant_ttc - f.montant_encaisse), 0);
    const enRetard = factures.filter((f) =>
      isPaiementEnRetard(mapStatut(f.statut), f.date_formation),
    ).length;
    return { caYTD, encaisseYTD, aRecouvrer, enRetard };
  }, [factures]);

  const chartData = useMemo(() => {
    const byYear = new Map<number, { facture: number; encaisse: number }>();
    for (const f of factures) {
      const cur = byYear.get(f.annee) ?? { facture: 0, encaisse: 0 };
      cur.facture += f.montant_ttc;
      cur.encaisse += f.montant_encaisse;
      byYear.set(f.annee, cur);
    }
    return Array.from(byYear.entries())
      .map(([annee, d]) => ({ annee, ...d }))
      .sort((a, b) => a.annee - b.annee);
  }, [factures]);

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selectedIds.size === factures.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(factures.map((f) => f.id)));
  };

  const exportSelectionCSV = (): void => {
    const rows = factures.filter((f) => selectedIds.has(f.id));
    if (rows.length === 0) {
      toast.warning('Aucune ligne sélectionnée');
      return;
    }
    const headers = [
      'Année',
      'Réf facture',
      'Formation',
      'Date',
      'Lieu',
      'Entreprise',
      'Participants',
      'HT',
      'TVA',
      'TTC',
      'Encaissé',
      'Statut',
      'Date règlement',
      'Mode',
      'Référence',
      'Observation',
    ];
    const csvLines = rows.map((f) =>
      [
        f.annee,
        f.reference_facture,
        f.formation_titre ?? '',
        f.date_formation ?? '',
        f.lieu ?? '',
        f.entreprise_cliente_nom,
        f.participants,
        f.montant_ht,
        f.montant_tva,
        f.montant_ttc,
        f.montant_encaisse,
        STATUT_LABEL[f.statut],
        f.date_reglement ?? '',
        f.mode_reglement ?? '',
        f.reference_virement ?? '',
        (f.observation ?? '').replace(/"/g, '""'),
      ]
        .map((v) => {
          const s = String(v ?? '');
          return /[",\n;]/.test(s) ? `"${s}"` : s;
        })
        .join(','),
    );
    const csv = [headers.join(','), ...csvLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recouvrement_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} ligne(s) exportée(s)`);
  };

  if (!canView) return <AccessDenied feature="recouvrement.view" />;

  return (
    <PageContainer>
      <ContextHero
        variant="crm"
        icon={<Receipt className="h-5 w-5" />}
        title="Recouvrement Entreprises"
        subtitle="Créances clients B2B — facturation Algérie + cross-références automatiques vers la Caisse"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={effectiveEntrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              aria-label="Entreprise"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="h-3.5 w-3.5" /> Importer Excel
                </button>
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Nouvelle facture
                </button>
              </>
            )}
          </div>
        }
      />

      {/* KPIs */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="CA YTD"
          value={<MoneyAmount amount={kpis.caYTD} variant="kpi" />}
          variant="crm"
          loading={isLoading}
        />
        <KpiCard
          label="Encaissé YTD"
          value={<MoneyAmount amount={kpis.encaisseYTD} variant="kpi" />}
          variant="echeances"
          icon={<CheckCircle2 className="h-4 w-4" />}
          loading={isLoading}
        />
        <KpiCard
          label="À recouvrer"
          value={<MoneyAmount amount={kpis.aRecouvrer} variant="kpi" />}
          variant="finances"
          loading={isLoading}
          hint="Reste à encaisser (a_encaisser + partiel)"
        />
        <KpiCard
          label="En retard >90 j"
          value={<span className="text-3xl font-black tabular-nums">{kpis.enRetard}</span>}
          unit={kpis.enRetard > 1 ? 'factures' : 'facture'}
          variant="dashboard"
          loading={isLoading}
        />
      </div>

      {/* BarChart CA par année */}
      {chartData.length > 0 && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="text-sm font-bold text-slate-800">CA par année</h3>
            <span className="text-xs text-slate-500">
              {chartData.length} année{chartData.length > 1 ? 's' : ''} historique
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="annee" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(v) =>
                  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}M` : `${(v / 1000).toFixed(0)}k`
                }
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(v: number) => `${v.toLocaleString('fr-DZ')} DA`}
                labelFormatter={(l) => `Année ${l}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="facture" fill="#2563EB" name="Facturé TTC" radius={[3, 3, 0, 0]} />
              <Bar dataKey="encaisse" fill="#10B981" name="Encaissé" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filtres */}
      <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Année</label>
          <select
            value={annee === '' ? '' : annee}
            onChange={(e) => setAnnee(e.target.value === '' ? '' : Number(e.target.value))}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Toutes —</option>
            {yearsAvailable.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Statut</label>
          <div className="flex gap-1">
            {STATUT_FILTERS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatut(s.value)}
                className={cn(
                  'h-9 rounded-md border px-3 text-xs font-semibold transition-colors',
                  statut === s.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Commercial</label>
          <select
            value={commercialId}
            onChange={(e) => setCommercialId(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Tous —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.full_name ?? u.user_id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-slate-500">Recherche</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Entreprise, réf, formation…"
              className="h-9 w-64 pl-7"
            />
          </div>
        </div>
        {(annee !== '' || statut || commercialId || searchInput) && (
          <button
            type="button"
            onClick={() => {
              setAnnee('');
              setStatut('');
              setCommercialId('');
              setSearchInput('');
            }}
            className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-xs text-slate-500 hover:bg-slate-100"
          >
            <XCircle className="h-3 w-3" /> Effacer
          </button>
        )}
      </div>

      {/* Sticky bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-blue-300 bg-blue-50/95 px-4 py-2.5 shadow-md backdrop-blur">
          <span className="text-sm font-bold text-blue-900">
            {selectedIds.size} facture{selectedIds.size > 1 ? 's' : ''} sélectionnée
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <span className="text-xs text-blue-700">
            ({selectedFactures.filter((f) => f.statut !== 'regle').length} encaissable
            {selectedFactures.filter((f) => f.statut !== 'regle').length > 1 ? 's' : ''})
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkPayOpen(true)}
              disabled={selectedFactures.filter((f) => f.statut !== 'regle').length === 0}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700',
                selectedFactures.filter((f) => f.statut !== 'regle').length === 0 &&
                  'cursor-not-allowed opacity-50',
              )}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Encaisser en lot
            </button>
            <button
              type="button"
              onClick={() => setBulkRelance(true)}
              disabled={
                selectedFactures.filter((f) => f.statut !== 'regle' && f.tier_id).length === 0
              }
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700',
                selectedFactures.filter((f) => f.statut !== 'regle' && f.tier_id).length === 0 &&
                  'cursor-not-allowed opacity-50',
              )}
              title="Relancer les clients pour les factures impayées (tier requis)"
            >
              <Send className="h-3.5 w-3.5" /> Relancer
            </button>
            <button
              type="button"
              onClick={exportSelectionCSV}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkDelete(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100"
            >
              <XCircle className="h-3 w-3" /> Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="mt-5">
        <SectionTitle
          trailing={
            <span className="text-xs text-slate-500">
              {factures.length} facture{factures.length > 1 ? 's' : ''}
            </span>
          }
        >
          Factures
        </SectionTitle>
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {isLoading ? (
            <div className="space-y-1 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : factures.length === 0 ? (
            <div className="p-6">
              <EmptyStateModern
                variant="crm"
                icon={<Receipt className="h-6 w-6" />}
                title="Aucune facture"
                description="Importez un Excel consolidé ou créez une facture manuellement."
                ctaLabel="Importer Excel"
                onCtaClick={() => setImportOpen(true)}
              />
            </div>
          ) : (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/80 text-[10px] font-bold uppercase tracking-widest text-slate-600 backdrop-blur">
                  <tr>
                    <th className="px-2 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === factures.length && factures.length > 0}
                        onChange={toggleAll}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Année</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Réf</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Entreprise</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Formation</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">TTC</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Encaissé</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Reste</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Statut</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date règl.</th>
                    <th className="px-2 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {factures.map((f) => {
                    const enRetard = isPaiementEnRetard(mapStatut(f.statut), f.date_formation);
                    const reste = f.montant_ttc - f.montant_encaisse;
                    const rowTone = enRetard
                      ? 'bg-rose-50/40 border-l-2 border-l-rose-300'
                      : f.statut === 'regle'
                        ? 'bg-emerald-50/30'
                        : f.statut === 'partiel'
                          ? 'bg-amber-50/30'
                          : 'bg-white';
                    return (
                      <tr
                        key={f.id}
                        className={cn(
                          'border-b border-slate-100 transition-colors hover:bg-slate-50',
                          rowTone,
                        )}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(f.id)}
                            onChange={() => toggleSelect(f.id)}
                            aria-label={`Sélectionner ${f.reference_facture}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 tabular-nums">{f.annee}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px] text-slate-600">
                          {f.reference_facture}
                        </td>
                        <td
                          className="cursor-pointer px-2 py-1.5 font-semibold text-slate-800 hover:underline"
                          onClick={() => setDrawerFacture(f)}
                        >
                          {f.entreprise_cliente_nom}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{f.formation_titre ?? '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {f.date_formation ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          <MoneyAmount amount={f.montant_ttc} />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          {f.montant_encaisse > 0 ? (
                            <MoneyAmount amount={f.montant_encaisse} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right font-semibold">
                          {reste > 0 ? (
                            <MoneyAmount amount={reste} />
                          ) : (
                            <span className="text-emerald-600">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <StatutPaiementBadge
                            statut={mapStatut(f.statut)}
                            enRetard={enRetard}
                            size="sm"
                          />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {f.date_reglement ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {f.statut !== 'regle' && (
                              <button
                                type="button"
                                onClick={() => setEncaisserFacture(f)}
                                className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2 text-xs font-semibold text-white hover:bg-emerald-700"
                                title="Encaisser"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                              </button>
                            )}
                            {f.statut !== 'regle' && f.tier_id && (
                              <button
                                type="button"
                                onClick={() => setRelanceFacture(f)}
                                className="inline-flex h-7 items-center gap-1 rounded-md bg-amber-600 px-2 text-xs font-semibold text-white hover:bg-amber-700"
                                title="Relancer le client"
                              >
                                <Send className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setPdfFacture(f)}
                              className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              title="Générer PDF"
                            >
                              <FileText className="h-3 w-3" />
                            </button>
                            {f.operation_id && (
                              <Badge variant="outline" className="text-[10px]">
                                Op
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals & drawer */}
      <ImportRecouvrementEntreprisesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
      />
      <CreateFactureRecouvrementDialog
        open={createOpen || editFacture !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditFacture(null);
          }
        }}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
        facture={editFacture}
      />
      <EnregistrerEncaissementDialog
        open={!!encaisserFacture}
        onOpenChange={(o) => !o && setEncaisserFacture(null)}
        facture={encaisserFacture}
      />
      <MarquerFacturesEncaisseesEnLotDialog
        open={bulkPayOpen}
        onOpenChange={setBulkPayOpen}
        factures={selectedFactures}
        entrepriseId={effectiveEntrepriseId}
        onDone={() => setSelectedIds(new Set())}
      />
      <GeneratePdfFactureDialog
        open={!!pdfFacture}
        onOpenChange={(o) => !o && setPdfFacture(null)}
        facture={pdfFacture}
      />

      <RelancerDialog
        open={!!relanceFacture || bulkRelance}
        onOpenChange={(o) => {
          if (!o) {
            setRelanceFacture(null);
            setBulkRelance(false);
          }
        }}
        targets={(relanceFacture
          ? [relanceFacture]
          : bulkRelance
            ? selectedFactures.filter((f) => f.statut !== 'regle' && f.tier_id)
            : []
        ).map(
          (row): RelanceTarget => ({
            kind: 'recouvrement',
            row,
          }),
        )}
        entrepriseId={effectiveEntrepriseId}
        onSent={() => {
          setRelanceFacture(null);
          setBulkRelance(false);
          setSelectedIds(new Set());
        }}
      />
      <FactureRecouvrementDrawer
        facture={drawerFacture}
        onClose={() => setDrawerFacture(null)}
        onEnregistrerEncaissement={(f) => {
          setEncaisserFacture(f);
          setDrawerFacture(null);
        }}
        onGeneratePdf={(f) => {
          setPdfFacture(f);
          setDrawerFacture(null);
        }}
        onEdit={(f) => {
          setEditFacture(f);
          setDrawerFacture(null);
        }}
        onDelete={(f) => {
          setConfirmDelete(f);
          setDrawerFacture(null);
        }}
        onRelancer={(f) => {
          setRelanceFacture(f);
          setDrawerFacture(null);
        }}
        onCreerPlan={(f) => {
          setPlanFacture(f);
          setDrawerFacture(null);
        }}
      />

      <PlanPaiementDepuisFactureDialog
        open={!!planFacture}
        onOpenChange={(o) => !o && setPlanFacture(null)}
        facture={planFacture}
        onSaved={() => setPlanFacture(null)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={
          confirmDelete
            ? `Supprimer la facture ${confirmDelete.reference_facture} ?`
            : 'Supprimer ?'
        }
        description={
          confirmDelete?.operation_id
            ? "Cette action est irréversible. L'opération Caisse liée sera également supprimée."
            : 'Cette action est irréversible.'
        }
        loading={deleteFacture.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteFacture.mutateAsync({
              id: confirmDelete.id,
              operation_id: confirmDelete.operation_id,
            });
            toast.success('Facture supprimée');
            setConfirmDelete(null);
          } catch (e) {
            toast.error(`Échec : ${(e as Error).message}`);
          }
        }}
      />
      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        destructive
        title={`Supprimer ${selectedIds.size} facture(s) ?`}
        description="Action irréversible. Les opérations Caisse liées seront aussi supprimées si présentes."
        loading={deleteFacture.isPending}
        onConfirm={async () => {
          let ok = 0;
          const errs: string[] = [];
          for (const f of selectedFactures) {
            try {
              await deleteFacture.mutateAsync({ id: f.id, operation_id: f.operation_id });
              ok++;
            } catch (e) {
              errs.push((e as Error).message);
            }
          }
          if (errs.length === 0) toast.success(`${ok} facture(s) supprimée(s)`);
          else toast.warning(`${ok} OK · ${errs.length} échec(s)`);
          setConfirmBulkDelete(false);
          setSelectedIds(new Set());
        }}
      />
    </PageContainer>
  );
}
